import React from "react";
import { FoundingBadge, getTierConfig } from "@/components/dashboard/FoundingBadge";
import { MemberCertificate } from "@/components/dashboard/MemberCertificate";
import { motion } from "framer-motion";
import { Share } from "@phosphor-icons/react";

export const FoundingSection = ({ user }) => {
    if (!user) return null;
    const cfg = getTierConfig(user.founder_tier);

    return (
        <section
            data-testid="founding-section"
            className="relative overflow-hidden rounded-lg border p-6 sm:p-8 lg:p-12"
            style={{ borderColor: cfg.ringColor, background: "#060606" }}
        >
            <div
                className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full blur-3xl"
                style={{ background: cfg.color, opacity: 0.08 }}
            />
            <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-[auto_1fr] lg:gap-16">
                <FoundingBadge
                    tier={user.founder_tier}
                    memberNumber={user.member_number}
                    size="lg"
                    testId="founding-section-badge"
                />

                <div>
                    <span
                        className="font-mono-metric text-[10px] uppercase tracking-[0.28em]"
                        style={{ color: cfg.color }}
                    >
                        {cfg.cap} · Permanent recognition
                    </span>
                    <motion.h2
                        initial={{ opacity: 0, y: 8 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="mt-4 font-editorial text-2xl leading-tight text-white sm:text-3xl md:text-4xl lg:text-5xl"
                        data-testid="founding-section-name"
                    >
                        {user.display_name},{" "}
                        <span className="italic" style={{ color: cfg.color }}>
                            #{String(user.member_number).padStart(4, "0")}.
                        </span>
                    </motion.h2>
                    <div className="mt-2 flex items-center gap-2">
                        <span className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                            Signed in as
                        </span>
                        <span
                            className="font-mono-metric text-[11px] uppercase tracking-[0.2em]"
                            style={{ color: cfg.color }}
                        >
                            {cfg.label}
                        </span>
                    </div>
                    <p className="mt-4 max-w-xl text-sm text-zinc-400 lg:text-base">
                        You joined before EVIQ was a household name. Your Founding badge is numbered,
                        non-transferable, and travels with you across the EVIQ interface — profile,
                        shared reports, coin, and certificate. #001 &amp; #002 are reserved for the
                        EVIQ founder; every other member starts at #003.
                    </p>

                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
                        {[
                            { label: "Member number", value: `#${String(user.member_number).padStart(4, "0")}` },
                            { label: "Joined", value: new Date(user.member_since).toLocaleDateString(undefined, { month: "short", year: "numeric" }) },
                            { label: "Trees at signup", value: `+${user.signup_trees_planted}` },
                        ].map((s) => (
                            <div
                                key={s.label}
                                className="rounded-md border border-[#1f1f22] bg-[#0a0a0a] px-4 py-3"
                            >
                                <div className="font-mono-metric text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                                    {s.label}
                                </div>
                                <div className="mt-1 font-mono-metric text-xl font-bold text-white">
                                    {s.value}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            data-testid="founding-share-btn"
                            className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-4 py-2 text-sm text-zinc-200 transition-colors duration-200 hover:border-[#3a3a3f] hover:text-white"
                        >
                            <Share size={14} />
                            Share my badge
                        </button>
                        <a
                            href="#member-certificate"
                            data-testid="founding-cert-btn"
                            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-black transition-transform duration-200 hover:-translate-y-0.5"
                            style={{ background: cfg.color }}
                        >
                            <Share size={14} weight="fill" />
                            Jump to certificate ↓
                        </a>
                    </div>
                </div>
            </div>
            <div id="member-certificate" className="scroll-mt-24">
                <MemberCertificate user={user} />
            </div>
        </section>
    );
};

export default FoundingSection;
